import { iconService } from "@/services/IconService";
import { imageService } from "@/services/ImageService";
import { toast } from "vue3-toastify";
import { i18n } from "@/lang";

let iconsRequest = null;

const state = {
    icons: [],
    iconsLoaded: false,
    productIcon: [],
    groupIcon: [],
    iconCategories: ["GROUP", "ALLERGEN"],
    isAddingIcon: false,
    getIconsRequest: {
        loading: false,
    },
    addIconRequest: {
        loading: false,
    },
    updateIconRequest: {
        loading: false,
    },
    deleteIconRequest: {
        loading: false,
    },
};

// nameLocalized ({en, fr, nl}, langues vides omises) peut être null → fallback sur name (toujours présent)
export function pictoDisplayName(picto) {
    if (!picto) return "";
    const localized = picto.nameLocalized;
    if (localized && typeof localized === "object") {
        const locale = typeof i18n.global.locale === "string"
            ? i18n.global.locale
            : i18n.global.locale.value;
        const fromLocalized = localized[locale] || localized.fr || localized.en || localized.nl;
        if (fromLocalized) return fromLocalized;
    }
    return picto.name || "";
}

const getters = {
    getIconsByCategory: (state) => (category) => {
        const currentIcons = Array.isArray(state.icons) ? state.icons : [];
        return currentIcons
            .filter(icon => icon.category === category)
            .map(icon => ({ ...icon, displayName: pictoDisplayName(icon) }));
    },

    getAllIcons(state) {
        const currentIcons = Array.isArray(state.icons) ? state.icons : [];
        return currentIcons.map(icon => ({ ...icon, displayName: pictoDisplayName(icon) }));
    },

    getAllergenIcons(state, getters) {
        return getters.getIconsByCategory("ALLERGEN");
    },

    getGroupIcons(state, getters) {
        return getters.getIconsByCategory("GROUP");
    }
};

const mutations = {
    setIsAddingIcon(state, value) {
        state.isAddingIcon = value;
    },
    setGetIconsRequest(state, { loading, message }) {
        state.getIconsRequest = {
            loading: loading,
            message: message,
        };
    },
    setAddIconRequest(state, { loading, message }) {
        state.addIconRequest = {
            loading: loading,
            message: message,
        };
    },
    setUpdateIconRequest(state, { loading, message }) {
        state.updateIconRequest = {
            loading,
            message,
        };
    },
    setDeleteIconRequest(state, { loading, message }) {
        state.deleteIconRequest = {
            loading: loading,
            message: message,
        };
    },
    setIconsLoaded(state, loaded) {
        state.iconsLoaded = loaded;
    },
    resetIcons(state) {
        state.icons = [];
        state.iconsLoaded = false;
    },
    updateIcons(state, newList) {
        if (Array.isArray(newList)) {
            state.icons = newList;
            return;
        }

        if (Array.isArray(newList?.data)) {
            state.icons = newList.data;
            return;
        }

        if (Array.isArray(newList?.icons)) {
            state.icons = newList.icons;
            return;
        }

        state.icons = [];
    },
    addIconToList(state, icon) {
        const currentIcons = Array.isArray(state.icons) ? state.icons : [];
        const normalizedIcon = icon?.data ? icon.data : icon;
        state.icons = [...currentIcons, normalizedIcon];
    },
    replaceIconInList(state, icon) {
        const currentIcons = Array.isArray(state.icons) ? state.icons : [];
        const normalizedIcon = icon?.data ? icon.data : icon;
        state.icons = currentIcons.map((element) =>
            element.pictoId === normalizedIcon.pictoId ? normalizedIcon : element
        );
    },
};

const actions = {
    ensureIcons({ state, dispatch }) {
        if (state.iconsLoaded) return Promise.resolve(state.icons);
        if (!iconsRequest) {
            iconsRequest = dispatch("fetchIcons").finally(() => {
                iconsRequest = null;
            });
        }
        return iconsRequest;
    },
    fetchIcons({ commit }) {
        commit("setGetIconsRequest", { loading: true });
        return iconService
            .getIcons()
            .then((response) => {
                commit("setGetIconsRequest", { loading: false });
                commit("updateIcons", response);
                commit("setIconsLoaded", true);
            })
            .catch((error) => {
                commit("setGetIconsRequest", { loading: false, message: error });
            });
    },

    fetchProductIcons({ commit }) {
        commit("setGetIconsRequest", { loading: true });
        iconService
            .getIconByCategory("ALLERGEN")
            .then((response) => {
                commit("setGetIconsRequest", { loading: false });
                commit("updateIcons", response);
            })
            .catch((error) => {
                commit("setGetIconsRequest", { loading: false, message: error });
            });
    },

    async addIcons({ commit }, iconList) {
        commit("setAddIconRequest", { loading: true });
        const failed = [];

        for (const icon of iconList) {
            try {
                const imageUrl = await imageService.uploadIconImage(icon.file);
                const response = await iconService.addIcon({
                    name: icon.name,
                    category: icon.category,
                    imageUrl,
                });
                commit("addIconToList", response);
            } catch (error) {
                failed.push(icon);
            }
        }

        commit("setAddIconRequest", { loading: false });

        const successCount = iconList.length - failed.length;
        if (successCount > 0) {
            toast.success(i18n.global.t("iconsPageStrings.addSuccess", { count: successCount }));
        }
        if (failed.length > 0) {
            toast.error(i18n.global.t("iconsPageStrings.addError", { count: failed.length }));
        }

        return failed;
    },

    async updateIcon({ commit }, { pictoId, name, nameLocalized }) {
        commit("setUpdateIconRequest", { loading: true });
        try {
            const response = await iconService.updateIcon(pictoId, { name, nameLocalized });
            commit("replaceIconInList", response);
            commit("setUpdateIconRequest", { loading: false });
            toast.success(i18n.global.t("toast.successEdit"));
            return true;
        } catch (error) {
            commit("setUpdateIconRequest", { loading: false, message: error });
            toast.error(i18n.global.t("toast.errorEdit"));
            return false;
        }
    },

    deleteIcon({ commit, state }, icon) {
        iconService
            .deleteIcon(icon)
            .then(() => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                const currentIcons = Array.isArray(state.icons) ? state.icons : [];
                const updatedList = currentIcons.filter(
                    (element) => element.pictoId !== icon.pictoId
                );
                commit("updateIcons", updatedList);
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.deleteError"));
                commit("setDeleteIconRequest", { loading: false, message: error });
            });
    },
};

export const icons = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
